import {
  AuthProviderProps,
  User,
  UserManager,
  WebStorageStateStore,
} from "oidc-react";
import { registerTokenExpiryHandlers } from "./token-expiry-handlers";

// Create UserManager instance
export const userManager = new UserManager({
  client_id: window.appConfig.REACT_APP_LANTBRUKARE_IAM_CLIENTID,
  post_logout_redirect_uri: `${window.location.origin}/`,
  redirect_uri: window.location.origin + "/authentication/callback",
  silent_redirect_uri: window.location.origin + "/authentication/silent-callback",
  scope: "openid email",
  authority: window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY,
  automaticSilentRenew: true,
  loadUserInfo: true,
  includeIdTokenInSilentSignout: true,
  accessTokenExpiringNotificationTimeInSeconds: 120, // Start refresh 2 min before expiry
  silentRequestTimeoutInSeconds: 30, // Allow 30s for silent renewal
  userStore: new WebStorageStateStore({ store: window.localStorage }),
  metadata: {
    issuer: `${window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY}`,
    authorization_endpoint: `${window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY}/protocol/openid-connect/auth`,
    token_endpoint: `${window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY}/protocol/openid-connect/token`,
    introspection_endpoint: `${window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY}/protocol/openid-connect/token/introspect`,
    end_session_endpoint: `${window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY}/protocol/openid-connect/logout`,
    revocation_endpoint: `${window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY}/protocol/openid-connect/revoke`,
    userinfo_endpoint: `${window.appConfig.REACT_APP_LANTBRUKARE_IAM_AUTHORITY}/protocol/openid-connect/userinfo`,
  },
});

registerTokenExpiryHandlers(userManager, "lantbrukare");

const lantbrukareAuthConfig: AuthProviderProps = {
  onSignIn: async (user: User | null) => {
    // Only redirect on actual OAuth callback (fresh login)
    // Skip redirect for session restore or silent token renewal
    const urlParams = new URLSearchParams(window.location.search);
    const hasAuthCode = urlParams.has('code');
    const isCallbackPage = window.location.pathname === '/authentication/callback';
    const isSilentCallback = window.location.pathname.includes('/silent-callback');

    if (!hasAuthCode || !isCallbackPage || isSilentCallback) {
      // Not a fresh OAuth callback - skip redirect
      return;
    }

    // Fresh login - redirect to appropriate page
    if (user && typeof user.state === "string") {
      window.location.replace(user.state);
    } else {
      window.location.replace("/dashboard");
    }
  },
  onSignInError: (error: Error) => {
    console.error("[Lantbrukare Auth] Sign in error:", error);

    // Check if this is a token renewal error (silent renewal) vs. initial sign-in error
    // Token renewal errors should be handled by the event handlers, not here
    const isTokenRenewalError = error.message?.toLowerCase().includes('silent') ||
                                 error.message?.toLowerCase().includes('renew');

    if (isTokenRenewalError) {
      // Token renewal error - don't clear storage, let event handlers retry
      console.warn("[Lantbrukare Auth] Token renewal failed, will retry via event handlers");
      return;
    }

    // Initial sign-in or callback error - redirect to login, returning the user
    // to the page they were on rather than dropping them on /dashboard
    console.warn("[Lantbrukare Auth] Sign-in failed, redirecting to login");
    userManager.signinRedirect({ state: window.location.href }).catch(() => {
      window.location.href = "/";
    });
  },
  onBeforeSignIn: () => {
    return window.location.href;
  },
  autoSignIn: true,
  // oidc-react defaults this to true, which makes AuthProvider register its own
  // silent-renew-error handler that calls signoutRedirect() to Keycloak. That is
  // a second, racing redirect away from the page; recovery is handled by the
  // expiry handler and autoSignIn instead (AG-282).
  autoSignOut: false,
  userManager: userManager,
};

export default lantbrukareAuthConfig;
